import {
  ApolloClient,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getServerUrl } from '@utils/environment';
import React, { FC, useMemo } from 'react';

import { useInviteCodeAuth } from './hooks/use-invite-code-auth';

type ApolloProviderContainerProps = {
  children: React.ReactNode;
};

export const ApolloProviderContainer: FC<ApolloProviderContainerProps> = ({
  children,
}) => {
  const { accessToken, inviteCode } = useInviteCodeAuth();

  const authLink = setContext(async (_, { headers }) => {
    if (accessToken) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${accessToken}`,
          ...(inviteCode ? { 'x-invite-code': inviteCode } : {}),
        },
      };
    }

    return {
      headers: {
        ...headers,
      },
    };
  });

  const client = useMemo(() => {
    const httpLink = new HttpLink({
      uri: `${getServerUrl()}/graphql`,
    });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          );
        });
      }
      if (networkError) console.error(`[Network error]: ${networkError}`);
    });

    return new ApolloClient({
      link: from([errorLink, authLink, httpLink]),
      cache: new InMemoryCache(),
    });
  }, [authLink]); // Consider adding process.env.REACT_APP_SERVER_URI here if it's not constant

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
